import {
  clearAuthStorage,
  getAccessToken,
  purgeLegacyAuthStorage,
  setAccessToken
} from './authStorage'
import { sessionLockService } from './sessionLockService'
import { tokenRefreshService } from './tokenRefreshService'

purgeLegacyAuthStorage()

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8081/api'

let unauthorizedHandler = null

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler
}

const handleUnauthorized = (message) => {
  if (typeof unauthorizedHandler === 'function') {
    unauthorizedHandler(message)
  }
}

export { getAccessToken, setAccessToken }

const buildUrl = (path, params = {}) => {
  const base = API_BASE_URL.startsWith('/')
    ? `${window.location.origin}${API_BASE_URL}`
    : API_BASE_URL
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${base}${normalizedPath}`)
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value)
    }
  })
  return url.toString()
}

const parseErrorMessage = async (response) => {
  try {
    const data = await response.json()
    return data.message || data.error || `Erreur HTTP ${response.status}`
  } catch {
    return `Erreur HTTP ${response.status}`
  }
}

export class ApiError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

const handleAuthFailure = async (message, { path, retry } = {}) => {
  if (sessionLockService.isLocked()) {
    throw new ApiError('Session verrouillée. Déverrouillez pour continuer.', 401)
  }

  if (retry && path !== '/auth/refresh') {
    const refreshed = await tokenRefreshService.refreshIfNeeded()
    if (refreshed) {
      return 'retry'
    }
  }

  handleUnauthorized(message)
  throw new ApiError(message, 401)
}

export const apiClient = {
  async request(path, { method = 'GET', body, params, auth = true, _retried = false } = {}) {
    const headers = {
      Accept: 'application/json'
    }

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    if (auth) {
      const token = getAccessToken()
      if (!token) {
        handleUnauthorized('Session expirée. Veuillez vous reconnecter.')
        throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401)
      }
      headers.Authorization = `Bearer ${token}`
    }

    const response = await fetch(buildUrl(path, params), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined
    })

    if (!response.ok) {
      const message = await parseErrorMessage(response)
      if (response.status === 401 && auth) {
        const result = await handleAuthFailure(message, { path, retry: !_retried })
        if (result === 'retry') {
          return this.request(path, { method, body, params, auth, _retried: true })
        }
      }
      throw new ApiError(message, response.status)
    }

    if (response.status === 204) {
      return null
    }

    return response.json()
  },

  get(path, params, options) {
    return this.request(path, { ...options, method: 'GET', params })
  },

  post(path, body, options) {
    return this.request(path, { ...options, method: 'POST', body })
  },

  put(path, body, options) {
    return this.request(path, { ...options, method: 'PUT', body })
  },

  patch(path, body, options) {
    return this.request(path, { ...options, method: 'PATCH', body })
  },

  delete(path, options) {
    return this.request(path, { ...options, method: 'DELETE' })
  },

  async downloadFile(path, filename, params) {
    const token = getAccessToken()
    if (!token) {
      handleUnauthorized('Session expirée. Veuillez vous reconnecter.')
      throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401)
    }

    const response = await fetch(buildUrl(path, params), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const message = await parseErrorMessage(response)
      if (response.status === 401) {
        const result = await handleAuthFailure(message, { path, retry: true })
        if (result === 'retry') {
          return this.downloadFile(path, filename, params)
        }
      }
      throw new ApiError(message, response.status)
    }

    const blob = await response.blob()
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
    const url = URL.createObjectURL(pdfBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename.replace(/[^\w.\- ()[\]]+/g, '_')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },

  async fetchBlob(path, params, options) {
    const headers = {}
    const token = getAccessToken()
    if (!token) {
      handleUnauthorized('Session expirée. Veuillez vous reconnecter.')
      throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401)
    }
    headers.Authorization = `Bearer ${token}`

    const response = await fetch(buildUrl(path, params), {
      method: 'GET',
      headers,
      ...options
    })

    if (!response.ok) {
      const message = await parseErrorMessage(response)
      if (response.status === 401) {
        const result = await handleAuthFailure(message, { path, retry: true })
        if (result === 'retry') {
          return this.fetchBlob(path, params, options)
        }
      }
      throw new ApiError(message, response.status)
    }

    const blob = await response.blob()
    return URL.createObjectURL(blob)
  },

  async upload(path, file, fieldName = 'file') {
    const token = getAccessToken()
    if (!token) {
      handleUnauthorized('Session expirée. Veuillez vous reconnecter.')
      throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401)
    }

    const formData = new FormData()
    formData.append(fieldName, file)

    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    })

    if (!response.ok) {
      const message = await parseErrorMessage(response)
      if (response.status === 401) {
        const result = await handleAuthFailure(message, { path, retry: true })
        if (result === 'retry') {
          return this.upload(path, file, fieldName)
        }
      }
      throw new ApiError(message, response.status)
    }

    return response.json()
  }
}

export const resetApiSession = () => {
  clearAuthStorage()
}
